import React from 'react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - MemU</title>
        <meta name="description" content="MemU Privacy Policy - Learn how we collect, use, and protect your data when using our AI memory integration services." />
        <meta name="keywords" content="memu privacy policy, data protection, ai privacy, memory service privacy, data security" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Privacy Policy - MemU" />
        <meta property="og:description" content="MemU Privacy Policy - Learn how we collect, use, and protect your data when using our AI memory integration services." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://memu.pro/privacy-policy" />
        <meta property="og:image" content="https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/memu-logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy - MemU" />
        <meta name="twitter:description" content="MemU Privacy Policy - Learn how we collect, use, and protect your data when using our AI memory integration services." />
        <meta name="twitter:image" content="https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/memu-logo.png" />
      </Helmet>
      
      <div className="privacy-page">
        <div className="container">
          <div className="privacy-content">
            <header className="privacy-header">
              <h1>Privacy Policy</h1>
              <p className="privacy-subtitle">Effective Date: October 1, 2025</p>
            </header>

            <div className="privacy-body">
              <p className="intro-text">
                At MemU Technologies, Inc. ("MemU," "we," "us," or "our"), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI memory integration services, including our website, APIs, and related services (collectively, the "Services").
              </p>

              <section className="privacy-section">
                <h2>1. Information We Collect</h2>
                
                <h3>1.1 Information You Provide Directly</h3>
                <p>
                  <strong>Account Information:</strong> When you create an account, we collect information such as your name, email address, username, and password.
                </p>
                <p>
                  <strong>Payment Information:</strong> When you purchase our services, we collect billing information such as your payment method details, billing address, and transaction history. Payment processing is handled by third-party processors who comply with PCI DSS standards.
                </p>
                <p>
                  <strong>Profile Information:</strong> You may choose to provide additional information such as profile pictures, preferences, and other optional details.
                </p>

                <h3>1.2 Information We Collect Automatically</h3>
                <p>
                  <strong>Usage Data:</strong> We collect information about how you interact with our Services, including API calls, feature usage, session duration, and performance metrics.
                </p>
                <p>
                  <strong>Device and Technical Information:</strong> We collect information about your device, browser type, operating system, IP address, and other technical identifiers.
                </p>
                <p>
                  <strong>Log Data:</strong> Our servers automatically record information about your use of our Services, including timestamps, error logs, and system performance data.
                </p>

                <h3>1.3 AI Memory Data</h3>
                <p>
                  <strong>Memory Content:</strong> When you use our AI memory services, we process and store the conversations, documents, and other content you choose to memorize through our platform.
                </p>
                <p>
                  <strong>Analysis Data:</strong> We may analyze your memory data to improve our services, generate insights, and provide personalized experiences, subject to your privacy settings.
                </p>
              </section>

              <section className="privacy-section">
                <h2>2. How We Use Your Information</h2>
                <p>We use the information we collect for the following purposes:</p>
                <ul>
                  <li><strong>Service Provision:</strong> To provide, maintain, and improve our AI memory services</li>
                  <li><strong>Account Management:</strong> To manage your account, authenticate users, and provide customer support</li>
                  <li><strong>Communication:</strong> To send you service-related notifications, updates, and marketing communications (with your consent)</li>
                  <li><strong>Personalization:</strong> To customize your experience and provide relevant recommendations</li>
                  <li><strong>Analytics and Research:</strong> To analyze usage patterns, improve our algorithms, and develop new features</li>
                  <li><strong>Security:</strong> To detect and prevent fraud, abuse, and security threats</li>
                  <li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our terms of service</li>
                </ul>
              </section>

              <section className="privacy-section">
                <h2>3. Information Sharing and Disclosure</h2>
                <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following limited circumstances:</p>

                <h3>3.1 Service Providers</h3>
                <p>
                  We may share information with trusted third-party service providers who assist us in operating our Services, such as cloud hosting providers, payment processors, and analytics services. These providers are contractually bound to protect your information.
                </p>

                <h3>3.2 Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction, subject to equivalent privacy protections.
                </p>

                <h3>3.3 Legal Requirements</h3>
                <p>
                  We may disclose your information if required by law, court order, or government request, or to protect our rights, property, or safety, or that of our users or the public.
                </p>

                <h3>3.4 Consent</h3>
                <p>
                  We may share your information with your explicit consent or at your direction.
                </p>
              </section>

              <section className="privacy-section">
                <h2>4. Data Security and Protection</h2>
                <p>
                  We implement industry-standard security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul>
                  <li>Encryption of data in transit and at rest using AES-256 and TLS protocols</li>
                  <li>Multi-factor authentication and access controls for our systems</li>
                  <li>Regular security audits and vulnerability assessments</li>
                  <li>Employee security training and background checks</li>
                  <li>Incident response procedures for data breaches</li>
                </ul>
                <p>
                  However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                </p>
              </section>

              <section className="privacy-section">
                <h2>5. Your Rights and Choices</h2>
                <p>You have the following rights regarding your personal information:</p>

                <h3>5.1 Access and Portability</h3>
                <p>
                  You can access and download your personal information through your account settings or by contacting us.
                </p>

                <h3>5.2 Correction and Updates</h3>
                <p>
                  You can update your account information and preferences at any time through your account settings.
                </p>

                <h3>5.3 Deletion</h3>
                <p>
                  You can request deletion of your account and associated data. Some information may be retained for legal or operational purposes as described in our data retention policy.
                </p>

                <h3>5.4 Marketing Communications</h3>
                <p>
                  You can opt out of marketing communications by using the unsubscribe link in our emails or updating your communication preferences.
                </p>

                <h3>5.5 Data Processing Restrictions</h3>
                <p>
                  You may request restrictions on how we process your data, subject to legal and contractual obligations.
                </p>
              </section>

              <section className="privacy-section">
                <h2>6. Cookies and Tracking Technologies</h2>
                <p>
                  We use cookies and similar technologies to enhance your experience, analyze usage, and provide personalized content. You can control cookie preferences through your browser settings. Essential cookies required for service functionality cannot be disabled.
                </p>
                <p>
                  We may also use analytics services like Google Analytics to understand how users interact with our Services. You can opt out of analytics tracking through various browser extensions or opt-out tools provided by analytics providers.
                </p>
              </section>

              <section className="privacy-section">
                <h2>7. Third-Party Services and Integrations</h2>
                <p>
                  Our Services may integrate with third-party platforms and services (such as OpenAI, Anthropic, and other AI providers). When you use these integrations, your data may be processed by these third parties according to their own privacy policies. We encourage you to review the privacy policies of any third-party services you connect to our platform.
                </p>
              </section>

              <section className="privacy-section">
                <h2>8. Data Retention</h2>
                <p>
                  We retain your personal information for as long as necessary to provide our Services and fulfill the purposes outlined in this Privacy Policy. Specific retention periods include:
                </p>
                <ul>
                  <li><strong>Account Information:</strong> Retained until account deletion, plus up to 30 days for account recovery</li>
                  <li><strong>Memory Data:</strong> Retained according to your subscription plan and deletion requests</li>
                  <li><strong>Usage Data:</strong> Typically retained for 24 months for analytics and service improvement</li>
                  <li><strong>Legal Data:</strong> May be retained longer as required by law or for legal proceedings</li>
                </ul>
              </section>

              <section className="privacy-section">
                <h2>9. International Data Transfers</h2>
                <p>
                  Our Services are operated from the United States, and we may transfer, store, and process your information in the United States and other countries. We ensure adequate protection for international transfers through appropriate safeguards such as Standard Contractual Clauses or adequacy decisions.
                </p>
              </section>

              <section className="privacy-section">
                <h2>10. Children's Privacy</h2>
                <p>
                  Our Services are not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
                </p>
              </section>

              <section className="privacy-section">
                <h2>11. California Privacy Rights</h2>
                <p>
                  If you are a California resident, you may have additional rights under the California Consumer Privacy Act (CCPA) and other California privacy laws, including the right to know what personal information we collect and how it's used, the right to delete personal information, and the right to opt out of the sale of personal information (note: we do not sell personal information).
                </p>
              </section>

              <section className="privacy-section">
                <h2>12. Changes to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by posting the updated policy on our website and, where appropriate, through other communication channels. Your continued use of our Services after the effective date of the revised Privacy Policy constitutes acceptance of the changes.
                </p>
              </section>

              <section className="privacy-section">
                <h2>13. Contact Us</h2>
                <p>
                  If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:
                </p>
                <p>
                  <strong>MemU Technologies, Inc.</strong><br />
                  Email: info@nevamind.ai<br />
                </p>
                <p>
                  We will respond to your inquiry within 30 days of receipt.
                </p>
              </section>

              <footer className="privacy-footer">
                <p>
                  <strong>Last Updated:</strong> October 1, 2025
                </p>
                <p>
                  This Privacy Policy is part of our Terms of Service and Service Credits Terms.
                </p>
              </footer>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .privacy-page {
          min-height: 100vh;
          padding: 150px 0 40px;
          background: var(--light);
          color: var(--dark);
        }

        .privacy-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .privacy-header {
          margin-bottom: 40px;
          text-align: center;
          padding-bottom: 20px;
          border-bottom: 2px solid var(--primary);
        }

        .privacy-header h1 {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .privacy-subtitle {
          font-size: 1.1rem;
          color: var(--dark-60);
          font-weight: 500;
        }

        .intro-text {
          font-size: 1.1rem;
          line-height: 1.8;
          margin-bottom: 30px;
          padding: 20px;
          background: var(--light-blue);
          border-radius: 8px;
          border-left: 4px solid var(--primary);
        }

        .privacy-section {
          margin-bottom: 30px;
          padding: 25px 0;
          border-bottom: 1px solid var(--gray);
        }

        .privacy-section:last-child {
          border-bottom: none;
        }

        .privacy-section h2 {
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 20px;
          color: var(--primary-dark);
        }

        .privacy-section h3 {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 10px;
          margin-top: 20px;
          color: var(--dark-blue);
        }

        .privacy-section p {
          font-size: 1rem;
          line-height: 1.7;
          margin-bottom: 15px;
          color: var(--dark-80);
        }

        .privacy-section p:last-child {
          margin-bottom: 0;
        }

        .privacy-section ul {
          margin: 15px 0;
          padding-left: 20px;
        }

        .privacy-section li {
          font-size: 1rem;
          line-height: 1.7;
          margin-bottom: 8px;
          color: var(--dark-80);
        }

        .privacy-section strong {
          color: var(--dark);
          font-weight: 600;
        }

        .privacy-footer {
          margin-top: 40px;
          padding-top: 30px;
          border-top: 2px solid var(--primary);
          text-align: center;
        }

        .privacy-footer p {
          font-size: 0.95rem;
          color: var(--dark-60);
          margin-bottom: 10px;
        }

        .privacy-footer p:last-child {
          margin-bottom: 0;
        }

        /* Mobile Responsiveness */
        @media (max-width: 768px) {
          .privacy-page {
            padding: 150px 0 30px;
          }

          .privacy-content {
            padding: 0 15px;
          }

          .privacy-header h1 {
            font-size: 2rem;
          }

          .intro-text {
            font-size: 1rem;
            padding: 15px;
          }

          .privacy-section {
            padding: 20px 0;
          }

          .privacy-section h2 {
            font-size: 1.2rem;
          }

          .privacy-section h3 {
            font-size: 1.1rem;
          }

          .privacy-section p, .privacy-section li {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </>
  );
};

export default PrivacyPolicy;
